import React, { Component } from "react";
import {
  Button,
  ButtonGroup,
  Col,
  ControlLabel,
  FormControl,
  FormGroup,
  Grid as Container,
  Row
} from "react-bootstrap";

import Card from "components/Card/Card.jsx";
import ViewportAlert from "components/ViewportAlert/ViewportAlert";
import {
  ALARM_SEVERITIES,
  TANK_STATES,
  formatAlarmNumber,
  getAlarmSeverity,
  getTankState
} from "utils/alarmPresentation";

const API_BASE =
  process.env.REACT_APP_API_BASE || "https://hgsystem.pl/mangusta";
const REFRESH_INTERVAL = 30000;

function dateInputValue(date) {
  const pad = value => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}`;
}

function initialDates() {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
  return {
    from: dateInputValue(from),
    to: dateInputValue(now)
  };
}

class Alarms extends Component {
  constructor(props) {
    super(props);
    const dates = initialDates();
    const query = new URLSearchParams(
      props.location ? props.location.search : ""
    );
    this.state = {
      mode: "active",
      deviceId: query.get("device") || "",
      severity: "",
      tankState: "",
      acknowledged: "all",
      from: dates.from,
      to: dates.to,
      alarms: [],
      total: 0,
      counts: {},
      loading: false,
      actionAlarmId: null,
      error: "",
      message: "",
      lastUpdatedAt: null
    };
  }

  componentDidMount() {
    this.mounted = true;
    this.loadAlarms();
    this.refreshTimer = window.setInterval(
      this.refreshAlarms,
      REFRESH_INTERVAL
    );
  }

  componentWillUnmount() {
    this.mounted = false;
    if (this.refreshTimer) window.clearInterval(this.refreshTimer);
  }

  post = body => {
    if (!this.props.credentials)
      return Promise.reject(new Error("Brak danych uwierzytelniających."));
    return fetch(`${API_BASE}/alarms.php`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        auth: this.props.credentials,
        ...body
      })
    }).then(async response => {
      const data = await response.json().catch(() => ({}));
      if (!response.ok)
        throw new Error(data.error || "Nie udało się wykonać operacji.");
      return data;
    });
  };

  getDateRange() {
    if (this.state.mode !== "history") return {};
    const fromDate = new Date(`${this.state.from}T00:00:00`);
    const toDate = new Date(`${this.state.to}T23:59:59`);
    if (
      Number.isNaN(fromDate.getTime()) ||
      Number.isNaN(toDate.getTime()) ||
      toDate < fromDate
    )
      return null;
    return {
      from: Math.floor(fromDate.getTime() / 1000),
      to: Math.min(
        Math.floor(toDate.getTime() / 1000),
        Math.floor(Date.now() / 1000) + 21600
      )
    };
  }

  getListRequest() {
    const range = this.getDateRange();
    if (range === null) return null;
    const request = {
      action: "list",
      status: this.state.mode === "active" ? "active" : "resolved",
      limit: 500,
      offset: 0,
      ...range
    };
    if (this.state.deviceId) request.device_id = this.state.deviceId;
    if (this.state.severity) request.severity = this.state.severity;
    if (this.state.tankState !== "")
      request.tank_state = this.state.tankState;
    if (this.state.acknowledged !== "all")
      request.acknowledged = this.state.acknowledged === "yes";
    return request;
  }

  loadAlarms = options => {
    const request = this.getListRequest();
    if (!request) {
      this.setState({ error: "Wybierz prawidłowy zakres dat." });
      return;
    }
    if (!this.props.credentials || this.state.loading) return;

    const quiet = options && options.quiet;
    this.setState({
      loading: true,
      error: "",
      message: quiet ? this.state.message : ""
    });
    this.post(request)
      .then(data => {
        if (!this.mounted) return;
        const alarms = Array.isArray(data.alarms) ? data.alarms : [];
        this.setState({
          alarms,
          total:
            data.total === undefined || data.total === null
              ? alarms.length
              : Number(data.total),
          counts: data.counts || {},
          loading: false,
          lastUpdatedAt: new Date()
        });
      })
      .catch(error => {
        if (this.mounted)
          this.setState({ loading: false, error: error.message });
      });
  };

  refreshAlarms = () => {
    if (!this.state.loading && this.state.actionAlarmId === null)
      this.loadAlarms({ quiet: true });
  };

  changeMode = mode => {
    if (mode === this.state.mode) return;
    this.setState(
      {
        mode,
        expandedAlarmId: null,
        error: "",
        message: ""
      },
      this.loadAlarms
    );
  };

  changeFilter = (field, value, reload) => {
    this.setState(
      {
        [field]: value,
        error: "",
        message: ""
      },
      reload ? this.loadAlarms : undefined
    );
  };

  acknowledge = alarm => {
    const alarmId = alarm.id;
    if (alarmId === undefined || alarmId === null) return;
    this.setState({
      actionAlarmId: alarmId,
      error: "",
      message: ""
    });
    this.post({
      action: "acknowledge",
      alarm_id: alarmId
    })
      .then(() => {
        if (!this.mounted) return;
        this.setState({
          actionAlarmId: null,
          message: "Alarm został potwierdzony."
        });
        this.loadAlarms({ quiet: true });
      })
      .catch(error => {
        if (this.mounted)
          this.setState({
            actionAlarmId: null,
            error: error.message
          });
      });
  };

  formatDate = timestamp => {
    if (timestamp === null || timestamp === undefined || timestamp === "")
      return "—";
    const date = new Date(Number(timestamp) * 1000);
    return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString("pl-PL");
  };

  getDevices() {
    const devices = Array.isArray(this.props.device) ? this.props.device : [];
    return devices.filter(
      (device, index, allDevices) =>
        allDevices.findIndex(
          item => String(item.id) === String(device.id)
        ) === index
    );
  }

  getDeviceName(alarm) {
    if (alarm.device_name) return alarm.device_name;
    const device = this.getDevices().find(
      item => String(item.id) === String(alarm.device_id)
    );
    return device ? device.desc || device.id : alarm.device_id || "—";
  }

  getSeverity(alarm) {
    return getAlarmSeverity(alarm.severity);
  }

  getTankState(alarm) {
    return getTankState(alarm.tank_state);
  }

  // Opens temperature and milk measurements with six hours of context
  // before and after the selected alarm occurrence.
  openAlarmChart = alarm => {
    const raisedAt = Number(alarm.raised_at);
    if (
      !this.props.history ||
      !alarm.device_id ||
      !Number.isFinite(raisedAt)
    )
      return;
    const contextSeconds = 6 * 60 * 60;
    const parameters = new URLSearchParams({
      device: String(alarm.device_id),
      from: String(Math.max(0, raisedAt - contextSeconds)),
      to: String(raisedAt + contextSeconds),
      alarm: String(alarm.id)
    });
    this.props.history.push(`/charts?${parameters.toString()}`);
  };

  getAlarmMessage(alarm) {
    return (
      alarm.message ||
      alarm.title ||
      alarm.code ||
      alarm.alarm_code ||
      "Alarm urządzenia"
    );
  }

  getDiagnosticMeasurements(alarm) {
    let diagnostics =
      alarm.diagnostics === undefined ? alarm.details : alarm.diagnostics;
    if (typeof diagnostics === "string") {
      try {
        diagnostics = JSON.parse(diagnostics);
      } catch (error) {
        diagnostics = null;
      }
    }
    if (!diagnostics || typeof diagnostics !== "object") return [];
    const measurements = [];
    if (
      diagnostics.temperature !== undefined &&
      diagnostics.temperature !== null
    )
      measurements.push({
        label: "Temperatura",
        value: `${formatAlarmNumber(diagnostics.temperature)} °C`
      });
    if (diagnostics.volume !== undefined && diagnostics.volume !== null)
      measurements.push({
        label: "Objętość",
        value: `${formatAlarmNumber(diagnostics.volume)} l`
      });
    return measurements;
  }

  renderFilters(devices) {
    const history = this.state.mode === "history";
    return (
      <Card
        title="Alarmy"
        category="Alarmy zgłoszone przez dostępne urządzenia"
        content={
          <div>
            <ButtonGroup style={{ marginBottom: 18 }}>
              <Button
                bsStyle={history ? "default" : "danger"}
                active={!history}
                disabled={this.state.loading}
                onClick={() => this.changeMode("active")}
              >
                Aktywne
              </Button>
              <Button
                bsStyle={history ? "info" : "default"}
                active={history}
                disabled={this.state.loading}
                onClick={() => this.changeMode("history")}
              >
                Historia
              </Button>
            </ButtonGroup>
            <Row>
              <Col sm={6} md={3}>
                <FormGroup>
                  <ControlLabel>Urządzenie</ControlLabel>
                  <FormControl
                    componentClass="select"
                    value={this.state.deviceId}
                    disabled={this.state.loading}
                    onChange={event =>
                      this.changeFilter(
                        "deviceId",
                        event.target.value,
                        true
                      )
                    }
                  >
                    <option value="">Wszystkie urządzenia</option>
                    {devices.map(device => (
                      <option value={String(device.id)} key={device.id}>
                        {device.desc || device.id}
                      </option>
                    ))}
                  </FormControl>
                </FormGroup>
              </Col>
              <Col sm={6} md={3}>
                <FormGroup>
                  <ControlLabel>Ważność</ControlLabel>
                  <FormControl
                    componentClass="select"
                    value={this.state.severity}
                    disabled={this.state.loading}
                    onChange={event =>
                      this.changeFilter(
                        "severity",
                        event.target.value,
                        true
                      )
                    }
                  >
                    <option value="">Wszystkie poziomy</option>
                    {Object.keys(ALARM_SEVERITIES).map(key => (
                      <option value={key} key={key}>
                        {ALARM_SEVERITIES[key].label}
                      </option>
                    ))}
                  </FormControl>
                </FormGroup>
              </Col>
              <Col sm={6} md={3}>
                <FormGroup>
                  <ControlLabel>Potwierdzenie</ControlLabel>
                  <FormControl
                    componentClass="select"
                    value={this.state.acknowledged}
                    disabled={this.state.loading}
                    onChange={event =>
                      this.changeFilter(
                        "acknowledged",
                        event.target.value,
                        true
                      )
                    }
                  >
                    <option value="all">Wszystkie</option>
                    <option value="no">Niepotwierdzone</option>
                    <option value="yes">Potwierdzone</option>
                  </FormControl>
                </FormGroup>
              </Col>
              <Col sm={6} md={3}>
                <FormGroup>
                  <ControlLabel>Stan zbiornika</ControlLabel>
                  <FormControl
                    componentClass="select"
                    value={this.state.tankState}
                    disabled={this.state.loading}
                    onChange={event =>
                      this.changeFilter(
                        "tankState",
                        event.target.value,
                        true
                      )
                    }
                  >
                    <option value="">Wszystkie stany</option>
                    {Object.keys(TANK_STATES).map(key => (
                      <option value={key} key={key}>
                        {TANK_STATES[key].label}
                      </option>
                    ))}
                  </FormControl>
                </FormGroup>
              </Col>
              <Col sm={6} md={3}>
                <Button
                  bsStyle="info"
                  block
                  style={{ marginTop: 25 }}
                  disabled={this.state.loading}
                  onClick={this.loadAlarms}
                >
                  {this.state.loading ? "Pobieranie…" : "Odśwież"}
                </Button>
              </Col>
            </Row>
            {history && (
              <Row>
                <Col sm={6} md={3}>
                  <FormGroup>
                    <ControlLabel>Od</ControlLabel>
                    <FormControl
                      type="date"
                      value={this.state.from}
                      disabled={this.state.loading}
                      onChange={event =>
                        this.changeFilter("from", event.target.value, false)
                      }
                    />
                  </FormGroup>
                </Col>
                <Col sm={6} md={3}>
                  <FormGroup>
                    <ControlLabel>Do</ControlLabel>
                    <FormControl
                      type="date"
                      value={this.state.to}
                      disabled={this.state.loading}
                      onChange={event =>
                        this.changeFilter("to", event.target.value, false)
                      }
                    />
                  </FormGroup>
                </Col>
                <Col sm={6} md={3}>
                  <Button
                    bsStyle="primary"
                    block
                    style={{ marginTop: 25 }}
                    disabled={this.state.loading}
                    onClick={this.loadAlarms}
                  >
                    Zastosuj daty
                  </Button>
                </Col>
              </Row>
            )}
            <div className="text-muted" style={{ fontSize: 12 }}>
              Lista odświeża się automatycznie co 30 sekund.
              {this.state.lastUpdatedAt
                ? ` Ostatnia aktualizacja: ${this.state.lastUpdatedAt.toLocaleTimeString(
                    "pl-PL"
                  )}.`
                : ""}
            </div>
          </div>
        }
      />
    );
  }

  renderAlarmCards() {
    return this.state.alarms.map(alarm => {
      const severity = this.getSeverity(alarm);
      const tankState = this.getTankState(alarm);
      const measurements = this.getDiagnosticMeasurements(alarm);
      const acknowledged =
        alarm.acknowledged_at !== null &&
        alarm.acknowledged_at !== undefined &&
        alarm.acknowledged_at !== "";
      const active = !alarm.resolved_at;
      return (
        <div
          key={`alarm-${alarm.id}`}
          style={{
            marginBottom: 12,
            padding: "14px 16px 13px 18px",
            border: "1px solid #dce4e8",
            borderLeft: `5px solid ${severity.color}`,
            borderRadius: 8,
            background: `linear-gradient(90deg, ${severity.color}0d, #ffffff 34%)`,
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: 8
            }}
          >
            <div>
              <strong style={{ fontSize: 16 }}>
                {this.getDeviceName(alarm)}
              </strong>
              <div style={{ color: "#687780", fontSize: 12, marginTop: 2 }}>
                <span role="img" aria-label="Data i czas">
                  🕒
                </span>{" "}
                {this.formatDate(alarm.raised_at)}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "flex-end",
                gap: 6
              }}
            >
              <span
                style={{
                  padding: "3px 9px",
                  borderRadius: 12,
                  color: severity.color,
                  backgroundColor: `${severity.color}18`,
                  border: `1px solid ${severity.color}55`,
                  fontSize: 11,
                  fontWeight: "bold"
                }}
              >
                {severity.label.toUpperCase()}
              </span>
              <span
                style={{
                  padding: "3px 9px",
                  borderRadius: 12,
                  color: active ? "#a93226" : "#287c45",
                  backgroundColor: active ? "#fdeceb" : "#e7f6ec",
                  fontSize: 11,
                  fontWeight: "bold"
                }}
              >
                {active ? "AKTYWNY" : "ZAKOŃCZONY"}
              </span>
            </div>
          </div>

          <div
            style={{
              marginTop: 10,
              marginBottom: 10,
              fontSize: 14,
              lineHeight: 1.45
            }}
          >
            <strong>{this.getAlarmMessage(alarm)}</strong>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {measurements.map(measurement => (
              <div
                key={measurement.label}
                style={{
                  flex: "1 1 130px",
                  padding: "7px 9px",
                  borderRadius: 5,
                  backgroundColor: "#f5f8fa",
                  border: "1px solid #e4eaee"
                }}
              >
                <div style={{ color: "#71808a", fontSize: 11 }}>
                  {measurement.label}
                </div>
                <strong>{measurement.value}</strong>
              </div>
            ))}
            <div
              style={{
                flex: "1 1 150px",
                padding: "7px 9px",
                borderRadius: 5,
                background: `linear-gradient(135deg, ${tankState.color}20, #ffffff)`,
                border: `1px solid ${tankState.color}70`
              }}
            >
              <div style={{ color: "#71808a", fontSize: 11 }}>
                Stan zbiornika
              </div>
              <strong style={{ color: tankState.color }}>
                {tankState.label}
              </strong>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 8,
              marginTop: 10,
              paddingTop: 9,
              borderTop: "1px solid #e6ecef"
            }}
          >
            <div style={{ color: "#71808a", fontSize: 11 }}>
              {acknowledged ? (
                <span>
                  Potwierdzono {this.formatDate(alarm.acknowledged_at)}
                  {alarm.acknowledged_by_name || alarm.acknowledged_by_login
                    ? ` · ${alarm.acknowledged_by_name ||
                        alarm.acknowledged_by_login}`
                    : ""}
                </span>
              ) : (
                "Niepotwierdzony"
              )}
              {!active &&
                ` · Zakończono ${this.formatDate(alarm.resolved_at)}`}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6
              }}
            >
              <Button
                bsStyle="primary"
                bsSize="small"
                onClick={() => this.openAlarmChart(alarm)}
              >
                Wykres
              </Button>
              {!acknowledged && (
                <Button
                  bsStyle="success"
                  bsSize="small"
                  disabled={this.state.actionAlarmId !== null}
                  onClick={() => this.acknowledge(alarm)}
                >
                  {String(this.state.actionAlarmId) === String(alarm.id)
                    ? "Zapisywanie…"
                    : "Potwierdź"}
                </Button>
              )}
            </div>
          </div>
        </div>
      );
    });
  }

  render() {
    const devices = this.getDevices();
    return (
      <div className="content">
        <Container fluid>
          <Row>
            <Col md={12}>{this.renderFilters(devices)}</Col>
          </Row>
          {this.state.error && (
          <ViewportAlert
            bsStyle="danger"
            onDismiss={() => this.setState({ error: "" })}
          >
            {this.state.error}
          </ViewportAlert>
          )}
          {this.state.message && (
          <ViewportAlert
            bsStyle="success"
            onDismiss={() => this.setState({ message: "" })}
          >
            {this.state.message}
          </ViewportAlert>
          )}
          <Row>
            <Col md={12}>
              <Card
                title={
                  this.state.mode === "active"
                    ? `Aktywne alarmy (${this.state.total})`
                    : `Historia alarmów (${this.state.total})`
                }
                category={
                  this.state.mode === "active"
                    ? "Bieżące problemy wymagające uwagi"
                    : "Zakończone zdarzenia alarmowe"
                }
                content={
                  <div>
                    {this.renderAlarmCards()}
                    {!this.state.loading &&
                      this.state.alarms.length === 0 && (
                        <div className="text-center text-muted">
                          {this.state.mode === "active"
                            ? "Brak aktywnych alarmów spełniających filtry."
                            : "Brak alarmów w wybranym okresie."}
                        </div>
                      )}
                    {this.state.loading &&
                      this.state.alarms.length === 0 && (
                        <div className="text-center text-muted">
                          Pobieranie alarmów…
                        </div>
                      )}
                  </div>
                }
              />
            </Col>
          </Row>
        </Container>
      </div>
    );
  }
}

export default Alarms;



// WEBPACK FOOTER //
// ./src/views/Alarms/Alarms.jsx